// sessionResume.js — Konversi detail sesi (respons /api/ai/chat-history/session/:id)
// menjadi bentuk `messages` yang dipahami AIChat.
//
// Modul MURNI: tidak meng-import komponen, context, maupun api. Satu-satunya
// import adalah aiConfig.js, yang sendirinya tidak meng-import apa pun (nol
// baris import di file itu) — jadi kemurnian tetap terjaga. Itu disengaja —
// pemakainya ada di dua lapisan berbeda (components/AI/AIPanel.js dan
// pages/AICoding.js, pages/AIChatHistory.js), dan modul yang meng-import
// komponen akan menciptakan jalur melingkar antara pages/ dan components/.
// Pola yang sama sudah dipakai routesMeta.js untuk alasan yang sama persis.
//
// Sebelumnya fungsi ini disalin di tiga tempat. Ketiganya bergantung pada bentuk
// `data` dari SATU endpoint, jadi setiap perubahan bentuk menuntut tiga suntingan
// yang serempak — dan yang terlewat tidak menimbulkan error, hanya tabel yang
// diam-diam berhenti tampil di satu halaman.

import { AI_PROVIDERS } from './aiConfig';

// Ambang pemotongan id sesi untuk penanda batas. Sesi id berbentuk
// 'sess_<base36 waktu>_<acak>'; 12 karakter terakhir sudah cukup unik untuk
// dicocokkan manusia dengan daftar riwayat, tanpa memenuhi satu baris.
const ID_TAIL = 12;

// Penanda batas diterjemahkan di sini, bukan lewat translations.js: modul ini
// harus tetap MURNI (tanpa import context/hook), sedangkan t() hanya tersedia di
// dalam komponen. Pemanggil meneruskan `lang` yang sudah dipegangnya.
//
// `{id}` diganti belakangan supaya potongan session_id tidak ikut diterjemahkan.
const NOTE = {
  id: '↩ Melanjutkan sesi `{id}`{by} — konteks diambil dari AI Service, bukan dari transkrip di atas.',
  en: '↩ Resuming session `{id}`{by} — context comes from the AI Service, not from the transcript above.',
  ar: '↩ استئناف الجلسة `{id}`{by} — يأتي السياق من خدمة الذكاء الاصطناعي، لا من النص أعلاه.',
};

// Klausa "dijawab oleh X" — TERPISAH dari NOTE karena bisa absen seluruhnya.
// Menyatukannya berarti setiap locale butuh dua varian kalimat (dengan dan
// tanpa model), dan varian yang terlewat baru ketahuan saat sesi lama dibuka.
const BY = {
  id: ' · dijawab oleh **{model}**',
  en: ' · answered by **{model}**',
  ar: ' · تمت الإجابة بواسطة **{model}**',
};

// Nama provider yang terbaca manusia. `meta.provider` berisi KEY mentah
// ('bedrock', 'groq'), bukan nama tampilan. Key yang tidak dikenal dipakai apa
// adanya — lebih baik menampilkan 'openai_custom' daripada menelan informasinya.
function providerLabel(key) {
  const k = String(key || '').trim();
  if (!k) return '';
  return AI_PROVIDERS[k]?.name || k;
}

// Deskripsi model historis dari meta sesi.
//
// Dikosongkan bila provider DAN model dua-duanya tidak ada. Itu keadaan yang
// sah, bukan kesalahan: baris peran user dan baris error sengaja tidak menulis
// provider/model (keduanya bukan panggilan model), jadi sesi yang belum pernah
// dijawab sama sekali memang tidak punya nilai ini.
export function describeModel(meta) {
  const prov  = providerLabel(meta?.provider);
  const model = String(meta?.model || '').trim();
  if (!prov && !model) return '';
  if (prov && model)   return `${prov} · ${model}`;
  return prov || model;
}

export function toChatMessages(detail, lang = 'id') {
  const out = (detail?.messages || []).map(m => ({
    role: m.message_role === 'user' ? 'user'
        : m.message_role === 'error' ? 'error'
        : 'assistant',
    content: m.content || '',
    // [REVIEW] id baris ai_chat_history + rating 👍/👎 milik user ini —
    // dipakai FeedbackButtons di AIChat (backend/aiReview.js).
    id:       m.id || null,
    myRating: m.my_rating || 0,
    tableRows: Array.isArray(m.data?.rows) ? m.data.rows : null,
    sources:   Array.isArray(m.data?.sources) ? m.data.sources : [],
    hasData:   !!(m.data?.rows && m.data.rows.length),
    // [THUMB] Jejak lampiran gambar pada pesan user. Tanpa pemetaan ini, sesi
    // yang dilanjutkan atau dibuka dari riwayat kehilangan gambarnya lagi —
    // persis masalah yang hendak diperbaiki.
    images:    Array.isArray(m.data?.images) && m.data.images.length ? m.data.images : null,
    // [TEXT ATTACH] Jejak berkas teks. Alasannya persis sama dengan images:
    // tanpa pemetaan ini, sesi yang dibuka kembali kehilangan tanda bahwa
    // pertanyaannya menyertakan berkas, dan transkripnya jadi menyesatkan.
    files:     Array.isArray(m.data?.files) && m.data.files.length ? m.data.files : null,
    // [TOOLS] Jejak tool yang dipanggil AI Coding pada jawaban ini. Dipetakan
    // di sini karena alasan yang sama dengan images: tanpa ini bukti pemanggilan
    // tool hilang begitu sesi dilanjutkan, dan yang tersisa hanya klaim model.
    tools:     Array.isArray(m.data?.tools) && m.data.tools.length ? m.data.tools : null,
    toolsExpected: !!m.data?.tools_expected,

    // ── Metrik: snake_case (DB) → camelCase (yang dibaca AIChat) ────────────
    // Tanpa pemetaan ini statistik sesi menjumlahkan `m.promptTokens` yang
    // selalu undefined, lalu `|| 0` membuatnya nol — bar sesi menampilkan
    // "0↑ 0↓ = 0 tokens" dan "Avg 0.0s" untuk sesi yang jelas-jelas berisi.
    // Nilainya SUDAH dikirim endpoint detail; dulu hanya dibuang di sini.
    promptTokens:     m.tokens_prompt     || 0,
    completionTokens: m.tokens_completion || 0,
    totalTokens:      m.tokens_total      || 0,
    responseTime:     m.response_time     || 0,
    // Penanda '~' pada badge token. Di DB kolomnya INTEGER 0/1, di AIChat
    // dibaca sebagai boolean — dikonversi di sini, bukan dibiarkan angka,
    // supaya `estimated={msg.tokensEstimated}` tidak menerima 0 yang truthy-nya
    // benar tapi tipenya menyimpang dari jalur pesan baru.
    tokensEstimated:  !!m.tokens_estimated,
  }));

  // Penanda batas, supaya jelas mana yang riwayat dan mana giliran baru.
  //
  // Kalimat keduanya bukan hiasan: transkrip di layar TIDAK dikirim ke model.
  // Konteks percakapan hidup di AI Service dan dibaca ulang per giliran dari
  // session_id. Tanpa catatan ini, menghapus sebagian transkrip di layar tampak
  // seperti menghapus ingatan model — padahal tidak.
  const tail  = String(detail?.session_id || '').slice(-ID_TAIL);
  const shown = describeModel(detail?.meta);
  const by    = shown ? (BY[lang] || BY.id).replace('{model}', shown) : '';
  out.push({
    role: 'system-note',
    content: (NOTE[lang] || NOTE.id).replace('{id}', tail).replace('{by}', by),
  });
  return out;
}

export default toChatMessages;
